import { useEffect, useState } from "react";
import { api, fmtCurrency, STAGES } from "@/lib/api";
import { useCurrencies, currencySymbol, currencyCodesFrom } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { Users, Globe, TrendingUp, FileCheck2, ArrowUpRight, CircleDot } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const currencies = useCurrencies();
  const [stats, setStats] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);

  const load = () => Promise.all([
    api.get("/stats").then((r) => setStats(r.data)),
    api.get("/my-stats").then((r) => setMyStats(r.data)),
    api.get("/activities", { params: { limit: 10 } }).then((r) => setActivities(r.data)),
    api.get("/deals").then((r) => setDeals(r.data)),
  ]).catch(() => {});

  useEffect(() => { load(); }, []);

  if (!stats || !myStats) return <div style={{ color: "var(--text-2)" }}>Loading…</div>;

  const myDeals = deals.filter((c) => c.sales_person_id === user?.id);
  const totalStaged = Object.values(stats.stages).reduce((a, b) => a + b, 0) || 1;
  const ratePct = Math.round((myStats.commission_rate || 0) * 1000) / 10;
  const myCurrencyCodes = currencyCodesFrom(myStats.total_quoted_value, myStats.won_quotes_value, myStats.commission_earned);
  const pipelineCurrencyCodes = currencyCodesFrom(stats.pipeline_fob_value, stats.pipeline_cif_value, stats.pipeline_exworks_value, stats.won_fob_value);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Private My Performance card */}
      <section
        className="card-ledger stagger"
        style={{ padding: "24px 28px", background: "linear-gradient(135deg, var(--primary-light) 0%, #fff 70%)", border: "1px solid #F4D2BB" }}
        data-testid="my-performance-card"
      >
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 28, alignItems: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: 8, background: user?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Cabinet Grotesk", fontWeight: 800, fontSize: 20 }}>
            {(user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2)}
          </div>
          <div>
            <div className="overline" style={{ fontSize: 10 }}>Your private stats · only you can see this</div>
            <div className="font-display" style={{ fontWeight: 700, fontSize: 22, marginTop: 2 }}>{user?.name}&rsquo;s Performance</div>
            <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 4 }}>
              <span data-testid="my-quotes-count">{myStats.quotes_count}</span> quotes prepared
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="overline" style={{ fontSize: 10 }}>Commission Rate</div>
            <div className="font-mono-data" style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }} data-testid="commission-rate">{ratePct}%</div>
          </div>
        </div>
        {myCurrencyCodes.length > 0 && (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginTop: 20, paddingTop: 20, borderTop: "1px solid #F4D2BB" }}>
            {myCurrencyCodes.map((code) => (
              <div key={code} style={{ padding: "12px 16px", background: "rgba(255,255,255,0.7)", borderRadius: 8, border: "1px solid #F4D2BB" }} data-testid={`my-stats-${code}`}>
                <div className="overline" style={{ fontSize: 10 }}>{code}</div>
                <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div className="flex justify-between gap-3">
                    <span>Quoted</span>
                    <span className="font-mono-data" style={{ color: "var(--text)", fontWeight: 600 }} data-testid={`my-quoted-value-${code}`}>
                      {fmtCurrency(myStats.total_quoted_value?.[code] || 0, code, currencySymbol(code, currencies))}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Closed-won</span>
                    <span className="font-mono-data" style={{ color: "var(--success)", fontWeight: 600 }} data-testid={`my-won-value-${code}`}>
                      {fmtCurrency(myStats.won_quotes_value?.[code] || 0, code, currencySymbol(code, currencies))}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Commission</span>
                    <span className="font-mono-data" style={{ color: "var(--primary)", fontWeight: 700 }} data-testid={`commission-earned-${code}`}>
                      {fmtCurrency(myStats.commission_earned?.[code] || 0, code, currencySymbol(code, currencies))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Hero pipeline value bar (team-wide) */}
      <section className="card-ledger stagger" style={{ padding: "28px 32px", display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "start" }} data-testid="pipeline-value-card">
        <div>
          <div className="overline">Team Pipeline · Active Value</div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 6, marginBottom: 16 }}>
            Across <strong style={{ color: "var(--text)" }}>{stats.total_customers}</strong> accounts
          </div>
          {pipelineCurrencyCodes.length > 0 ? (
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              {pipelineCurrencyCodes.map((code) => (
                <div key={code} className="card-ledger" style={{ padding: 18, background: "var(--surface-2)" }} data-testid={`pipeline-${code}`}>
                  <div className="overline" style={{ fontSize: 10 }}>{code} Pipeline</div>
                  <div className="font-mono-data" style={{ fontSize: 24, fontWeight: 700, color: "var(--text)", marginTop: 4 }} data-testid={`pipeline-fob-value-${code}`}>
                    FOB {fmtCurrency(stats.pipeline_fob_value?.[code] || 0, code, currencySymbol(code, currencies))}
                  </div>
                  <div className="font-mono-data" style={{ fontSize: 14, color: "var(--text-2)", marginTop: 2 }} data-testid={`pipeline-cif-value-${code}`}>
                    CIF {fmtCurrency(stats.pipeline_cif_value?.[code] || 0, code, currencySymbol(code, currencies))}
                  </div>
                  <div className="font-mono-data" style={{ fontSize: 14, color: "var(--text-2)", marginTop: 2 }} data-testid={`pipeline-exworks-value-${code}`}>
                    Ex-Works {fmtCurrency(stats.pipeline_exworks_value?.[code] || 0, code, currencySymbol(code, currencies))}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 8 }}>
                    <span style={{ color: "var(--success)", fontWeight: 600 }} data-testid={`won-fob-value-${code}`}>
                      {fmtCurrency(stats.won_fob_value?.[code] || 0, code, currencySymbol(code, currencies))}
                    </span>{" "}FOB closed-won
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "var(--text-2)" }} data-testid="pipeline-value">No pipeline value yet.</div>
          )}
        </div>
        <div>
          <div className="overline" style={{ marginBottom: 10 }}>Stage Distribution</div>
          <div style={{ display: "flex", height: 12, borderRadius: 2, overflow: "hidden", border: "1px solid var(--border)" }}>
            {STAGES.map((s) => {
              const v = stats.stages[s.key] || 0;
              const pct = (v / totalStaged) * 100;
              return v > 0 ? <div key={s.key} title={`${s.label}: ${v}`} style={{ width: `${pct}%`, background: s.accent }} /> : null;
            })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 12 }}>
            {STAGES.map((s) => (
              <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-2)" }}>
                <span style={{ width: 8, height: 8, background: s.accent, borderRadius: 2, display: "inline-block" }} />
                <span style={{ color: "var(--text)", fontWeight: 600 }}>{stats.stages[s.key] || 0}</span> {s.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team KPIs */}
      <section className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }} data-testid="kpi-grid">
        {[
          { label: "Total Customers", value: stats.total_customers, icon: Users, testid: "kpi-customers" },
          { label: "Countries", value: stats.countries, icon: Globe, testid: "kpi-countries" },
          { label: "Team Conversion", value: stats.conversion_rate + "%", icon: TrendingUp, testid: "kpi-conversion" },
          { label: "Team Quotations", value: stats.total_quotations, icon: FileCheck2, testid: "kpi-quotations" },
        ].map((k, i) => (
          <div key={k.label} className="card-ledger stagger" style={{ padding: 20, animationDelay: `${i * 60}ms` }} data-testid={k.testid}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline" style={{ fontSize: 10 }}>{k.label}</div>
              <k.icon size={16} strokeWidth={1.75} style={{ color: "var(--primary)" }} />
            </div>
            <div className="font-mono-data" style={{ fontSize: 30, fontWeight: 700, color: "var(--text)" }}>{k.value}</div>
          </div>
        ))}
      </section>

      {/* Top deals (your leads only) + team activity */}
      <section style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24 }}>
        <div className="card-ledger" data-testid="top-deals-card">
          <div className="flex items-center justify-between px-6 py-4 divider-h">
            <div>
              <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Top Active Leads</h2>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>By FOB value · your leads</div>
            </div>
            <Link to="/pipeline" className="btn-ghost" data-testid="view-all-customers">View all <ArrowUpRight size={14} /></Link>
          </div>
          <div>
            {myDeals.filter((c) => c.stage !== "won" && c.stage !== "lost").sort((a, b) => (b.fob_value || 0) - (a.fob_value || 0)).slice(0, 6).map((c, i) => (
              <div key={c.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 16, alignItems: "center", padding: "14px 24px", borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{c.title || c.requirements || "Untitled lead"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>{c.customer_name}</div>
                </div>
                <span className="tag" style={{ borderColor: "var(--border)", color: "var(--text-2)", background: "var(--surface-2)" }}>{c.stage}</span>
                <div className="font-mono-data" style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--primary)" }}>FOB {fmtCurrency(c.fob_value, c.currency, currencySymbol(c.currency, currencies))}</div>
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>CIF {fmtCurrency(c.cif_value, c.currency, currencySymbol(c.currency, currencies))}</div>
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>Ex-Works {fmtCurrency(c.exworks_value, c.currency, currencySymbol(c.currency, currencies))}</div>
                </div>
              </div>
            ))}
            {myDeals.filter((c) => c.stage !== "won" && c.stage !== "lost").length === 0 && (
              <div style={{ padding: 24, fontSize: 13, color: "var(--text-2)" }}>No active leads yet.</div>
            )}
          </div>
        </div>

        <div className="card-ledger" data-testid="activity-card">
          <div className="px-6 py-4 divider-h">
            <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Team Activity</h2>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2 }}>Who did what · transparent across the team</div>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>
            {activities.map((a) => (
              <li key={a.id} style={{ display: "flex", gap: 12, padding: "10px 24px" }} data-testid={`activity-${a.id}`}>
                <CircleDot size={14} style={{ color: "var(--primary)", marginTop: 3, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--text)" }}>
                    {a.by_name && <strong style={{ color: "var(--text)" }}>{a.by_name}: </strong>}
                    {a.message}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
            {activities.length === 0 && (
              <li style={{ padding: 24, fontSize: 13, color: "var(--text-2)" }}>No activity yet.</li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}
